import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import { ApprovalIntegration } from '../../../../components/generic';
import { renderSplitViewBadgeOrBoolean } from '../../../../components/generic/splitViewFieldDisplay';
import { riskAssessmentConfig } from '../../riskAssessmentConfig';
import { normalizeFileName } from '../../../../components/ui/utils/fileNameEncoding';

const RiskAssessmentDetailsTab = ({ 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields 
}) => {
  const { theme } = useTheme();

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      <div className="grid grid-cols-1 gap-4">
        
        {riskAssessmentConfig.shouldShowApprovalInDetails() && (
          <div className="mb-6">
            <ApprovalIntegration 
              entity={selectedRecord}
              entityType="risk_assessment"
              entityData={{
                riskAssessmentCode: selectedRecord.code,
                riskAssessmentTitle: selectedRecord.title,
                riskCategory: selectedRecord.riskCategory?.name,
                status: selectedRecord.status?.name,
                createdBy: selectedRecord.createdBy?.userDisplayName,
                createdAt: selectedRecord.createdAt,
                requestedDate: selectedRecord.requestedDate,
                receivedDate: selectedRecord.receivedDate
              }}
              onApprovalUpdate={(approval) => {
                console.log('Review and approval updated:', approval);
                // You can add additional logic here to refresh the risk assessment data
              }}
              customTitle="Risk Assessment Review and Approval Status"
              customButtonText={riskAssessmentConfig.approval.approvalButtonText}
              showEntityDetails={riskAssessmentConfig.approval.showApprovalStatus}
              showApprovalActions={riskAssessmentConfig.approval.showApprovalActions}
              showApprovalHistory={riskAssessmentConfig.approval.showApprovalHistory}
              formMode={riskAssessmentConfig.approval.formMode || 'standalone'}
              config={{
                defaultApprovalType: riskAssessmentConfig.approval.defaultApprovalType,
                allowMultipleApprovals: riskAssessmentConfig.approval.allowMultipleApprovals
              }}
            />
          </div>
        )}

        {config.formSections?.map((section, idx) => {
          // Handle custom component sections - they're handled separately
          if (section.layout === 'custom' && section.customComponent) {
            // Handle Actions section
            if (section.title === 'Actions') {
              return (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-3">
                    {section.icon && (
                      <span className="text-base" aria-hidden="true">{section.icon}</span>
                    )}
                    <div className={cn("text-sm font-semibold", theme.text.primary)}>
                      {section.title} {selectedRecord?.riskAssessmentActions?.length > 0 && `(${selectedRecord.riskAssessmentActions.length})`}
                    </div>
                  </div>
                  {selectedRecord?.riskAssessmentActions && selectedRecord.riskAssessmentActions.length > 0 ? (
                    <div className="space-y-3">
                      {selectedRecord.riskAssessmentActions.map((action, actionIdx) => (
                        <div
                          key={action.id || actionIdx}
                          className={cn(
                            "p-3 rounded-lg border",
                            theme.border.subtle,
                            theme.colors.backgroundAlt
                          )}
                        >
                          <div className="mb-2">
                            <div className={cn("text-sm font-medium", theme.text.primary)}>
                              Action #{actionIdx + 1}
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className={cn("font-medium", theme.text.secondary)}>Description: </span>
                              <span className={theme.text.primary}>{action.description || 'N/A'}</span>
                            </div>
                            {action.startedAt && (
                              <div>
                                <span className={cn("font-medium", theme.text.secondary)}>Started At: </span>
                                <span className={theme.text.primary}>
                                  {new Date(action.startedAt).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {action.dueDate && (
                              <div>
                                <span className={cn("font-medium", theme.text.secondary)}>Due Date: </span>
                                <span className={theme.text.primary}>
                                  {new Date(action.dueDate).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {action.closedAt && (
                              <div>
                                <span className={cn("font-medium", theme.text.secondary)}>Closed At: </span>
                                <span className={theme.text.primary}>
                                  {new Date(action.closedAt).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-4 text-sm", theme.text.muted)}>
                      No actions recorded
                    </div>
                  )}
                </div>
              );
            }
            // For other custom components, skip (they're handled in the form)
            return null;
          }
          
          // Skip sections without fields
          if (!section.fields || !Array.isArray(section.fields)) return null;
          
          return (
            <div key={idx}>
              <div className="flex items-center gap-2 mb-2">
                {section.icon && (
                  <span className="text-base" aria-hidden="true">{section.icon}</span>
                )}
                <div className={cn("text-sm font-semibold", theme.text.primary)}>{section.title}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.fields.map((fieldName) => {
                const f = fields.find(ff => ff.name === fieldName);
                if (!f || f.type === 'hidden') return null;
                
                let rawVal = selectedRecord[fieldName];
                const isEmpty = rawVal == null || rawVal === "";

                // Compute display value for select/lookups and special types
                let displayVal = rawVal;
                let isBadge = false;
                let isStatus = false;
                let isBoolean = false;

                // Try table.displayField (e.g., status, department)
                const displayField = f.table && f.table.displayField;
                if (displayField && selectedRecord[displayField] != null) {
                  displayVal = selectedRecord[displayField];
                  isBadge = f.table && (f.table.cellRenderer === 'badge' || f.table.cellRenderer === 'statusBadge');
                  isStatus = f.table && f.table.cellRenderer === 'statusBadge';
                } else if (/Id$/.test(f.name)) {
                  // Try related object common patterns
                  const base = f.name.replace(/Id$/, '');
                  const rel = selectedRecord[base];
                  if (rel && typeof rel === 'object') {
                    displayVal = rel.name || rel.title || rel.status || rel.label || displayVal;
                    isBadge = true;
                  }
                }

                // Special handling for array fields (responsibleForActionUsers, responsibleForActionRoles, etc.)
                if (fieldName === 'responsibleForActionUserIds' || fieldName === 'responsibleForActionUsers') {
                  const responsibleUsers = selectedRecord.responsibleForActionUsers || [];
                  return (
                    <div key={fieldName} className="md:col-span-2">
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                        {responsibleUsers.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {responsibleUsers.map((user, idx) => (
                              <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.info, theme.text.inverse)}>
                                {user.userDisplayName || user.name || user.userEmail || user.email || 'Unknown'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={cn(theme.text.muted)}>—</span>
                        )}
                      </div>
                    </div>
                  );
                }

                // Special handling for responsibleForActionRoles array
                if (fieldName === 'responsibleForActionRoleIds' || fieldName === 'responsibleForActionRoles') {
                  const responsibleRoles = selectedRecord.responsibleForActionRoles || [];
                  return (
                    <div key={fieldName} className="md:col-span-2">
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                        {responsibleRoles.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {responsibleRoles.map((role, idx) => (
                              <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.info, theme.text.inverse)}>
                                {role.name || role.title || 'Unknown'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={cn(theme.text.muted)}>—</span>
                        )}
                      </div>
                    </div>
                  );
                }

                // Special handling for responsibleForActionDepartments array
                if (fieldName === 'responsibleForActionDepartmentIds' || fieldName === 'responsibleForActionDepartments') {
                  const responsibleDepartments = selectedRecord.responsibleForActionDepartments || [];
                  return (
                    <div key={fieldName} className="md:col-span-2">
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                        {responsibleDepartments.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {responsibleDepartments.map((dept, idx) => (
                              <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.info, theme.text.inverse)}>
                                {dept.name || dept.title || 'Unknown'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={cn(theme.text.muted)}>—</span>
                        )}
                      </div>
                    </div>
                  );
                }

                // Special handling for involvedDepartments array
                if (fieldName === 'involvedDepartmentsIds' || fieldName === 'involvedDepartments') {
                  const involvedDepartments = selectedRecord.involvedDepartments || [];
                  return (
                    <div key={fieldName} className="md:col-span-2">
                      <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                      <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                        {involvedDepartments.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {involvedDepartments.map((dept, idx) => (
                              <span key={idx} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", theme.colors.info, theme.text.inverse)}>
                                {dept.name || dept.title || 'Unknown'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={cn(theme.text.muted)}>—</span>
                        )}
                      </div>
                    </div>
                  );
                }

                // Type-based rendering
                if ((f.type === 'switch') || f.type === 'boolean') {
                  isBoolean = true;
                } else if (f.type === 'date') {
                  try { 
                    if (!isEmpty) displayVal = new Date(rawVal).toLocaleDateString(); 
                  } catch {}
                }

                return (
                  <div key={fieldName} className={fieldName === 'rationale' ? 'md:col-span-2' : ''}>
                    <div className={cn("text-xs mb-1", theme.text.secondary)}>{f.label}</div>
                    <div className={cn("text-sm p-2 rounded flex items-center gap-2", theme.colors.background, theme.text.primary)}>
                      {renderSplitViewBadgeOrBoolean({
                        field: f,
                        record: selectedRecord,
                        theme,
                        rawVal,
                        displayVal,
                        isBadge,
                        isStatus,
                        isBoolean,
                      }) ?? (f.type === 'textarea' ? (
                        <span className="whitespace-pre-wrap">{String(displayVal ?? '—')}</span>
                      ) : (
                        <span className={cn(isEmpty && theme.text.muted)}>{String(displayVal ?? '—')}</span>
                      ))}
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          );
        })}
        
        {/* File Attachments Section */}
        {config.features?.fileAttachments && selectedRecordFiles && selectedRecordFiles.filter(file => !file.isDeleted).length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
              <div className={cn("text-sm font-semibold", theme.text.primary)}>
                File Attachments ({selectedRecordFiles.filter(file => !file.isDeleted).length})
              </div>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedRecordFiles.filter(file => !file.isDeleted).map((file, index) => (
                <div key={index} className={cn("flex items-center gap-3 p-3 rounded-lg border", theme.colors.background, theme.border.subtle)}>
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                      {normalizeFileName(file.fileName || '')}
                    </div>
                    <div className={cn("text-xs", theme.text.secondary)}>
                      {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                    </div>
                  </div>
                  <a 
                    href={API_ENDPOINTS.fileAttachment.download(file.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("text-xs px-3 py-1 rounded-md transition-colors", theme.colors.primary, theme.text.inverse, "hover:opacity-80")}
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessmentDetailsTab;
